import React from 'react';
import { styled } from 'theme';
import { FileUpload } from '../../store/slices/file';
import { FileUploadStatuses } from './UploadFileAsLink';
import Loading from '../Loading';
import { Icon } from '@mui/material';
import { Cancel } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

const Row = styled('div')({
    flexGrow: 1,
    display: 'inline-block',
});

const RowMain = styled('div')({
    'display': 'inline-block',
    'position': 'relative',
    'margin': '6px / 2',

    '&:hover .hover': {
        display: 'block',
    },
});

interface HoverProps {
    visible?: boolean;
}

const Hover = styled('div')<HoverProps>(({ theme, visible }) => ({
    'position': 'absolute',
    'display': visible ? 'block' : 'none',
    'justifyContent': 'center',
    'backgroundColor': alpha(theme.palette.primary.main, 0.35),
    'color': theme.palette.secondary.main,
    'border': `solid 2px ${theme.palette.secondary.main}`,
    'top': 0,
    'bottom': '6px',
    'left': 0,
    'right': 0,
    'borderRadius': '7px',
    'fontSize': '1.2em',
    'cursor': 'pointer',

    '& svg': {
        color: theme.palette.secondary.main,
        marginRight: '10px',
    },
}));

const UploadedPicture = styled('img')(({ theme }) => ({
    border: `solid 2px ${theme.palette.primary.main}`,
    borderRadius: '7px',
    maxHeight: '160px',
}));

const Spacer = styled('div')(({ theme }) => ({
    border: `solid 2px ${theme.palette.primary.main}`,
    borderRadius: '7px',
    maxHeight: '160px',
    padding: '15px 10px 10px 10px',
    maxWidth: '140px',
    height: '160px',
    fontSize: '0.8em',
    textAlign: 'center',
    overflow: 'hidden',
}));

const HoverCont = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
});


interface UploadFileProps {
    file: Partial<FileUpload>,
    label?: string,
    onDeleteFile?: () => void,
}

export const UploadFileAsItem: React.FC<UploadFileProps> = ({
    file,
    onDeleteFile,
}) => {

    const uploaded = [
        FileUploadStatuses.UPLOADED,
    ].find((s) => s === file.status)?.length;

    const uploading = file && [
        FileUploadStatuses.REQUESTED_UPLOAD,
        FileUploadStatuses.UPLOADING,
        FileUploadStatuses.UPLOAD_FINISHING,
    ].find((s) => s === file.status)?.length;

    const error = file && [
        FileUploadStatuses.REQUESTED_UPLOAD_FAIL,
        FileUploadStatuses.UPLOADING_FAIL,
        FileUploadStatuses.UPLOAD_FINISHING_FAIL,
        FileUploadStatuses.DELETE_FAIL,
    ].find((s) => s === file.status)?.length;

    const deleting = file && [
        FileUploadStatuses.DELETING,
    ].find((s) => s === file.status)?.length;

    const deleted = file && [
        FileUploadStatuses.DELETED,
    ].find((s) => s === file.status)?.length;

    if (deleted) {
        return null;
    }

    return (
        <Row key={file.fileName}>
            <RowMain>
                {!deleting && !uploading ? (
                    <Hover
                        className='hover'
                        onClick={() => {
                            if (onDeleteFile) {
                                onDeleteFile();
                            }
                        }}
                    >
                        <HoverCont>
                            <Icon className='icon'>
                                <Cancel/>
                            </Icon>
                            <span>delete</span>
                        </HoverCont>
                    </Hover>
                ) : null}
                {uploading ? <Spacer>Uploading...</Spacer> : null}
                {error ? <Spacer>File upload error</Spacer> : null}
                {deleting ? (
                    <Hover
                        visible={true}
                    >
                        <HoverCont>
                            <Loading text={'Deleting...'}/>
                        </HoverCont>
                    </Hover>
                ) : null}
                {uploaded || deleting ? (
                    <UploadedPicture src={file.url}/>
                ) : null}
            </RowMain>
        </Row>
    );
};